import { matchPath } from 'react-router-dom';
import type { IPermission } from '@condo/domain';
import type { RamRouteObject } from './types';

export interface RoutePermissionEntry {
    /** Full absolute pattern, e.g. `/assets/:id/tasks`. */
    pattern: string;
    /** Optional stable identifier copied from `RamRouteObject.name`. */
    name?: string;
    /**
     * Union of `permissions` declared on this node and every ancestor.
     * Already deduplicated.
     */
    permissions: IPermission[];
}

let entries: RoutePermissionEntry[] = [];
let byName: Map<string, IPermission[]> = new Map();

function joinPath(parent: string, segment: string | undefined): string {
    if (!segment) {
        return parent;
    }
    if (segment.startsWith('/')) {
        return normalize(segment);
    }
    return normalize(`${parent}/${segment}`);
}

function normalize(path: string): string {
    const collapsed = path.replace(/\/+/g, '/');
    if (collapsed.length > 1 && collapsed.endsWith('/')) {
        return collapsed.slice(0, -1);
    }
    return collapsed;
}

function unique(list: IPermission[]): IPermission[] {
    return Array.from(new Set(list));
}

/**
 * Walk the RAM route tree and emit one entry per node, with the
 * cumulative ALL-of permission set from root to that node. Patterns
 * are normalized to react-router-style absolute paths (with leading
 * slash, no trailing slash).
 */
export function buildRoutePermissionEntries(routes: RamRouteObject[]): RoutePermissionEntry[] {
    const out: RoutePermissionEntry[] = [];
    walk(routes, '', [], out);
    return out;
}

function walk(routes: RamRouteObject[], parentPath: string, parentPerms: IPermission[], out: RoutePermissionEntry[]): void {
    for (const route of routes) {
        const fullPath = joinPath(parentPath, route.path);
        const perms = unique([...parentPerms, ...(route.permissions ?? [])]);
        out.push({ pattern: fullPath || '/', name: route.name, permissions: perms });
        if (route.children?.length) {
            walk(route.children, fullPath, perms, out);
        }
    }
}

/**
 * Replace the module-level registry. Call once from `app/router.tsx`
 * after building the route tree.
 */
export function setRoutePermissionEntries(next: RoutePermissionEntry[]): void {
    entries = next;
    byName = new Map();
    for (const entry of next) {
        if (!entry.name) {
            continue;
        }
        if (byName.has(entry.name)) {
            console.warn(`[permissions] duplicate route name "${entry.name}" — first occurrence wins.`);
            continue;
        }
        byName.set(entry.name, entry.permissions);
    }
}

export function getRoutePermissionEntries(): RoutePermissionEntry[] {
    return entries;
}

/**
 * Return the cumulative permission union for a named route, or `[]`
 * if no entry uses this name. Prefer this over `getPermissionsForPath`
 * whenever the caller can carry a stable route name — it survives
 * URL refactors and skips pattern matching.
 */
export function getPermissionsByName(name: string): IPermission[] {
    return byName.get(name) ?? [];
}

/**
 * Return the cumulative permission union for the first entry that
 * matches `pathname`. Picks the most specific (longest pattern)
 * matching entry so leaf permissions win over ancestor patterns.
 *
 * Returns `[]` when no entry matches or the matched entry has no
 * permissions — callers should treat that as "open".
 */
export function getPermissionsForPath(pathname: string): IPermission[] {
    let best: RoutePermissionEntry | undefined;
    let bestLength = -1;
    for (const entry of entries) {
        if (matchPath({ path: entry.pattern, end: false }, pathname)) {
            if (entry.pattern.length > bestLength) {
                best = entry;
                bestLength = entry.pattern.length;
            }
        }
    }
    return best?.permissions ?? [];
}
